import { z } from 'zod';
/**
 * Administrative levels, coarse to fine (SAL-14). Germany fills all six from
 * BKG's VG250: Staat, Land, Regierungsbezirk, Kreis, Verwaltungsgemeinschaft,
 * Gemeinde. Other countries map their own units onto the same names; from
 * Natural Earth they have the country and its first level (`state`).
 */ export const REGION_LEVELS = [
    'country',
    'state',
    'government-region',
    'district',
    'association',
    'municipality'
];
export const RegionLevelSchema = z.enum(REGION_LEVELS);
/**
 * An administrative area as reference data of the platform (SAL-14) — the
 * area a public body is responsible for, with the figures an app filters by.
 * Read-only for apps; loaded per country from an official source.
 */ export const RegionSchema = z.object({
    /**
   * The ISO code for a country (`DE`, `US`), else `<country>:<scheme>:<code>`,
   * e.g. `DE:ARS:082310000000`, `US:NE:USA-3521`.
   */ id: z.string(),
    country: z.string().length(2),
    level: RegionLevelSchema,
    /** The German name where the source has one: Kalifornien, Vereinigte Staaten. */ name: z.string(),
    /** Further names a search finds it by — English, local, codes: California, CA, USA. */ altNames: z.array(z.string()),
    /** The official kind as the source names it: Stadt, Landkreis, Stadtkreis, Amt … */ kind: z.string(),
    /** The source's key: in Germany the Regionalschlüssel (ARS), elsewhere ISO 3166. */ officialKey: z.string(),
    /** Germany's Gemeindeschlüssel (AGS) where the level has one. */ ags: z.string().nullable(),
    parentId: z.string().nullable(),
    /** Every enclosing region, nearest first — filter "within" without walking up. */ ancestorIds: z.array(z.string()),
    population: z.number().int().nonnegative().nullable(),
    areaKm2: z.number().nonnegative().nullable(),
    nuts: z.string().nullable(),
    location: z.object({
        lat: z.number(),
        lon: z.number()
    }).nullable(),
    /** The source's reference date, ISO (`2025-12-31`). */ asOf: z.string(),
    source: z.string()
});
/** The most regions one search returns. */ export const REGION_SEARCH_MAX = 50;
/** The most regions one `getMany` reads. */ export const REGION_GET_MANY_MAX = 1000;
/**
 * A search by name or alternative name (case, umlauts and ß folded), exact
 * matches first, then names that start with it. Without `country` it searches
 * every country — how a country is found by its name. `within` keeps regions
 * that lie in the given region at any depth.
 */ export const RegionSearchSchema = z.object({
    name: z.string().trim().min(1).max(200),
    country: z.string().length(2).optional(),
    levels: z.array(RegionLevelSchema).min(1).optional(),
    within: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(REGION_SEARCH_MAX).default(10)
});
/** The most regions one `list` page returns. */ export const REGION_LIST_MAX = 2000;
/**
 * Regions by where they lie and how many live there — not by name: every
 * municipality of a state, every district between two population figures,
 * every country of the world (no `country`). Most populous first, a page at
 * a time.
 */ export const RegionListSchema = z.object({
    country: z.string().length(2).optional(),
    levels: z.array(RegionLevelSchema).min(1),
    within: z.string().min(1).optional(),
    minPopulation: z.number().int().nonnegative().optional(),
    maxPopulation: z.number().int().nonnegative().optional(),
    limit: z.number().int().min(1).max(REGION_LIST_MAX).default(500),
    offset: z.number().int().nonnegative().default(0)
});
/**
 * The key a region's name is searched by: lower case, umlauts and ß spelled
 * out, other accents dropped, punctuation to spaces — "Bad Dürkheim",
 * "bad duerkheim" and "Bad-Dürkheim" meet. Shared by the import and the
 * search so both agree.
 */ export function regionNameKey(name) {
    return name.toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss').normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
}

//# sourceMappingURL=region.schema.js.map